import api from "@/api";

const PROJECT_MEDIA_PAGE_SIZE = 1000;
const PROJECT_MEDIA_MAX_PAGES = 50;

// LSP-20270 review finding: getSnapshotsForProject/getFilesForProject now cap a single response
// at 1000 rows server-side. Several real production projects already have several thousand
// snapshots (verified against production before this fix — one exceeds 15,000), so a single
// unpaged call here would silently drop older media from any caller with no error. Page through
// every row instead — the same total data volume an unbounded call used to fetch in one response,
// just chunked across requests.
//
// Re-verification finding: the original version had no page cap and no dedupe, so if this admin
// build ever ran against an older/rolled-back API that ignores page/pageSize (returning every row
// unpaged on every "page"), each response would look like a full page and the loop would never
// terminate, re-fetching the same thousands of rows forever. A page cap bounds that to
// PROJECT_MEDIA_MAX_PAGES redundant fetches instead of an infinite loop (matching the Scribe
// orchestrator's equivalent fetchAllPages helper); dedupe by id then keeps the result correct even
// if that happens.
//
// MEDIUM re-verification finding (2026-09-24): comparing a page's length against the REQUESTED
// pageSize (1000) breaks if the server's MAX_PAGE_SIZE is ever configured below 1000 (e.g. for a
// bounded load-test window, see resolveMaxPageSize.ts) — every response would then look "short"
// and the loop would stop after page 1, silently dropping everything past the server's real cap.
// Compare against the size of the actual FIRST page received instead, since that's the server's
// real effective page size regardless of what pageSize was requested. The unavoidable cost: when
// the true total is an exact multiple of that effective size (most commonly: the project has
// fewer than 1000 rows and the server isn't capping), one extra, empty confirmation page is
// fetched before stopping — correctness over saving that one round trip.
async function fetchAllPagedRows(
	fetchPage: (page: number, pageSize: number) => Promise<any[]>,
	onCap: (fetched: number) => void
): Promise<any[]> {
	const all: any[] = [];
	const seenIds = new Set<string>();
	let effectivePageSize: number | undefined;
	for (let page = 1; page <= PROJECT_MEDIA_MAX_PAGES; page += 1) {
		const batch = await fetchPage(page, PROJECT_MEDIA_PAGE_SIZE);
		for (const row of batch) {
			const rowId = row?.id;
			if (typeof rowId === "string") {
				if (seenIds.has(rowId)) continue;
				seenIds.add(rowId);
			}
			all.push(row);
		}
		if (batch.length === 0) return all;
		if (effectivePageSize === undefined) {
			effectivePageSize = batch.length;
		} else if (batch.length < effectivePageSize) {
			return all;
		}
	}
	onCap(all.length);
	return all;
}

export async function fetchAllProjectSnapshots(projectId: string): Promise<any[]> {
	return fetchAllPagedRows(
		(page, pageSize) => api.getSnapshotsForProject(projectId, { page, pageSize }),
		(fetched) =>
			console.warn("[fetchAllProjectSnapshots] media pagination hit MAX_PAGES cap; results may be incomplete", {
				projectId,
				maxPages: PROJECT_MEDIA_MAX_PAGES,
				fetched
			})
	);
}

// LSP-20270 review finding (HIGH): EntityDocuments.vue and EntityDetailPage.vue called
// api.getFilesForProject(id) directly with no paging, so any project with more than 1000 files
// (the server-side cap) silently lost the older ones with no error surfaced anywhere.
export async function fetchAllProjectFiles(projectId: string): Promise<any[]> {
	return fetchAllPagedRows(
		(page, pageSize) => api.getFilesForProject(projectId, { page, pageSize }),
		(fetched) =>
			console.warn("[fetchAllProjectFiles] media pagination hit MAX_PAGES cap; results may be incomplete", {
				projectId,
				maxPages: PROJECT_MEDIA_MAX_PAGES,
				fetched
			})
	);
}
